import {
  BottomNavigation,
  Button,
  Confirmation,
  Typography,
  useForm,
} from '@otp/ui';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ContentWrapper } from 'src/components/common/wrapper/content/wrapper';
import {
  UserMergeRequestDetailsItem,
  UserMergeRequestForm as Form,
} from './types';
import { CardWrapper } from 'src/components/common/wrapper/card/wrapper';
import { UserMergeRequestForm } from './form/form';
import {
  CreateUserMergeRequestDto,
  UserMergeRequestListFilterInput,
  UserMergeRequestsDocument,
  UserMergeRequestStatus,
} from 'src/graphql/generated/graphql';
import { useNavigate } from 'react-router-dom';
import { Routes } from 'src/components/common/router/constants';
import { useUserMergeRequestCreate } from 'src/hooks/query/user-merge-requests/use-user-merge-request-create';
import { useUserMergeRequestUpdate } from 'src/hooks/query/user-merge-requests/use-user-merge-request-update';
import { UserMergeRequestsTable } from '../list/merge-requests/table';
import { UserMergeRequestListItem } from '../list/merge-requests/types';
import { useTable } from 'src/hooks/use-table';
import { useUserMergeRequests } from 'src/hooks/query/user-merge-requests/use-user-merge-requests';
import { useAuthContext } from 'src/context/auth.context';
import { Laptop } from '@otp/icons';
import { useUserMergeRequestValidate } from 'src/hooks/query/user-merge-requests/use-user-merge-request-validate';

const REFETCH_QUERIES = [UserMergeRequestsDocument];
const PAGINATION_LIMIT = 10;

export const UserMergeRequestItem = ({
  entity = {},
  hiddenFields = [],
  onFinished,
}: {
  entity?: Partial<UserMergeRequestDetailsItem>;
  entities?: Partial<UserMergeRequestListItem>[];
  hiddenFields?: string[];
  onFinished?: () => void;
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuthContext();
  const {
    control,
    watch,
    handleSubmit,
    formState: { errors },
    getValues,
  } = useForm<Form>({
    defaultValues: {
      userId: entity.userId as string,
      eIdsToMerge: entity.eIdsToMerge,
      previousStoreNumbers: entity.previousStoreNumbers || [],
      previousEmail: entity.previousEmail || '',
      reason: entity.reason,
      requestNotes: entity.requestNotes || '',
      responseNotes: entity.responseNotes || '',
    },
  });

  const [userId, setUserId] = useState(getValues('userId') || user?.id);
  const [waitingForApprovalRequestData, setWaitingForApprovalRequestData] =
    useState<CreateUserMergeRequestDto | null>(null);

  const { page, setPage, filter, onFilterChange } = useTable<
    UserMergeRequestListFilterInput,
    Record<any, any>
  >({ defaultFilter: { userId: userId } });
  const {
    loading: loadingHistory,
    data: historyData,
    totalCount,
  } = useUserMergeRequests({
    filter,
    pagination: {
      page,
      limit: PAGINATION_LIMIT,
    },
    skip: !userId,
  });

  useEffect(() => {
    onFilterChange({ userId: userId });
  }, [userId, onFilterChange]);

  const [validateUserMergeRequest, { loading: loadingValidate }] =
    useUserMergeRequestValidate();
  const [createUserMergeRequest, { loading: loadingCreate }] =
    useUserMergeRequestCreate(REFETCH_QUERIES);
  const [updateUserMergeRequest, { loading: loadingUpdate }] =
    useUserMergeRequestUpdate(REFETCH_QUERIES);

  const loading = loadingValidate || loadingCreate || loadingUpdate;

  const upsertUserMergeRequest = async (data: CreateUserMergeRequestDto) => {
    if (entity?.id) {
      await updateUserMergeRequest({
        variables: {
          id: entity.id,
          data,
        },
        refetchQueries: REFETCH_QUERIES,
      });
    } else {
      await createUserMergeRequest({
        variables: {
          data,
        },
        refetchQueries: REFETCH_QUERIES,
      });
    }

    onClose();
  };

  const onSubmit = async (formData: Form) => {
    const data = {
      status: UserMergeRequestStatus.Pending,
      ...formData,
      userId: hiddenFields.includes('userId') ? userId : formData.userId,
    };

    const isValid = (
      await validateUserMergeRequest({
        variables: {
          data,
        },
      })
    )?.data?.validateUserMergeRequest;

    if (isValid) {
      await upsertUserMergeRequest(data);
    } else {
      setWaitingForApprovalRequestData(data);
    }
  };

  const onClose = () => {
    if (onFinished) {
      onFinished();
    } else {
      navigate(Routes.userMergeRequests.path);
    }
  };

  return (
    <>
      <ContentWrapper>
        <form onSubmit={handleSubmit(onSubmit)}>
          <CardWrapper>
            <Typography
              variant="subtitle1"
              fontWeight="bold"
              color="primary.10"
              className="flex items-center uppercase"
            >
              <Laptop className="mr-2" />
              {t(
                entity.id
                  ? 'user-merge-requests.form.title-edit'
                  : 'user-merge-requests.form.title-create',
              )}
            </Typography>
            <UserMergeRequestForm
              control={control}
              watch={watch}
              errors={errors}
              hiddenFields={hiddenFields}
              displayEidsDetails={!!entity.id}
            />
          </CardWrapper>
          <CardWrapper className="mt-6">
            <Typography
              variant="subtitle1"
              fontWeight="bold"
              color="primary.10"
              className="flex items-center uppercase"
            >
              <Laptop className="mr-2" />
              {t('user-merge-requests.form.request-history')}
            </Typography>
            <UserMergeRequestsTable
              data={historyData as UserMergeRequestListItem[]}
              loading={loadingHistory}
              className="mt-4"
              hiddenColumns={['requestNotes', 'adminActions']}
              options={{
                page,
                count: totalCount,
                sort: true,
                rowsPerPage: PAGINATION_LIMIT,
                onChangePage: setPage,
                enableNestedDataAccess: '.',
              }}
            />
          </CardWrapper>

          <BottomNavigation>
            <Button
              className="mr-2"
              type="submit"
              variant="contained"
              disabled={loading}
              sx={{ minWidth: 128 }}
            >
              {t('common.form.buttons.save')}
            </Button>
            <Button className="mr-4" variant="outlined" onClick={onClose}>
              {t('common.form.buttons.cancel')}
            </Button>
          </BottomNavigation>
        </form>
      </ContentWrapper>
      {waitingForApprovalRequestData && (
        <Confirmation
          open={true}
          onClose={() => setWaitingForApprovalRequestData(null)}
          onConfirm={() => {
            upsertUserMergeRequest(waitingForApprovalRequestData);
          }}
          message={t('user-merge-requests.form.confirmation.message')}
          confirmButtonText={t(
            'user-merge-requests.form.confirmation.confirm-button-text',
          )}
          closeButtonText={t(
            'user-merge-requests.form.confirmation.close-button-text',
          )}
        />
      )}
    </>
  );
};
